// Underline-style tabs (Stripe / Vercel / Plain pattern). Used for major
// sub-page navigation within a resource (e.g. agent playground/settings).
//
// Why not the shadcn-default pill style: pill tabs feel like a toggle / option
// group (List vs Grid). For navigation between sub-pages of an entity, the
// underline reads as "section nav" — lighter weight, continuous baseline that
// flows into the card below, no awkward gray island.
//
// On mobile the strip is `flex w-full`, so 4 short labels fit naturally without
// the squashed-pill effect of forcing grid columns. Overflow scrolls
// horizontally for edge cases (>4 tabs or <375px viewports) — the continuous
// bottom border keeps it readable when partially scrolled.

import * as React from 'react';
import * as TabsPrimitive from '@radix-ui/react-tabs';
import { cn } from '../../lib/utils.js';

export const Tabs = TabsPrimitive.Root;

export const TabsList = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.List>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.List
    ref={ref}
    className={cn(
      'flex w-full items-center justify-start overflow-x-auto border-b border-border bg-transparent text-muted-foreground [&::-webkit-scrollbar]:hidden',
      className,
    )}
    style={{ scrollbarWidth: 'none' }}
    {...props}
  />
));
TabsList.displayName = TabsPrimitive.List.displayName;

export const TabsTrigger = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>
>(({ className, ...props }, ref) => (
  // -mb-px aligns the trigger's 2px bottom border with the parent's 1px
  // border-b so the active underline visually replaces (not stacks under)
  // the baseline. min-w-0 lets the trigger shrink in narrow containers.
  <TabsPrimitive.Trigger
    ref={ref}
    className={cn(
      'relative -mb-px inline-flex min-w-0 items-center justify-center whitespace-nowrap border-b-2 border-transparent px-4 py-2.5 text-sm font-medium text-muted-foreground ring-offset-background transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:border-primary data-[state=active]:text-foreground',
      className,
    )}
    {...props}
  />
));
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName;

export const TabsContent = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cn(
      'mt-4 ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
      className,
    )}
    {...props}
  />
));
TabsContent.displayName = TabsPrimitive.Content.displayName;
